import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, Bot, Camera, Loader2, Phone as PhoneIcon, ArrowRight, UserPlus, UploadCloud, Check as CheckIcon, X as XIcon, Mic, Clock } from 'lucide-react';
import Dropzone from './Dropzone';
import TranscribePage from './TranscribeSaveEventModal';
import EmbeddedQuickAddContact from './EmbeddedQuickAddContact';
import { supabase } from '../lib/supabase';
import { listContactsCore, listContactsForGroupCore, createContactCore } from '../lib/contactsCore';
import { useGroupContext } from '../lib/groups/context';
import { getQuickActionsConfig } from '../lib/quickActionsConfig';
// Transcription logic moved into TranscribePage; Assistant only renders it now
import { createEvent } from '../lib/events';

type Role = 'user' | 'assistant' | 'system';
type Mode = 'contacts' | 'ai' | 'scan' | 'phone' | 'create' | 'transcribe';

interface ChatItem {
    id: string;
    role: Role;
    content: string;
    mode: Mode;
    attachments?: Array<{ type: 'contact' | 'image'; payload: any }>; // contact cards, images
    meta?: any;
}

export default function ChatAssist({ user }: { user: any }) {
    const [mode, setMode] = useState<Mode>('contacts');
    const [input, setInput] = useState('');
    const [messages, setMessages] = useState<ChatItem[]>([]);
    const [busy, setBusy] = useState(false);
    const { activeGroupId } = useGroupContext();
    const containerRef = useRef<HTMLDivElement | null>(null);
    // Tools state removed; buttons are always visible in compact header
    // Contact suggestions state
    const [suggestions, setSuggestions] = useState<any[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const debounceRef = useRef<NodeJS.Timeout | null>(null);
    // Scan mode camera state
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const [scanning, setScanning] = useState(false);
    const [phoneDigits, setPhoneDigits] = useState('');

    // Cross-mode selected contacts (for Ask AI context, Create, Transcribe save)
    const [selectedContacts, setSelectedContacts] = useState<any[]>([]);

    // Transcribe logic removed from Assistant: now handled by TranscribePage

    // History
    type HistoryEntry = { id: string; type: 'contacts' | 'ai' | 'create' | 'transcribe'; title: string; subtitle?: string; ts: number; payload?: any };
    const readHistory = (): HistoryEntry[] => {
        try { return JSON.parse(localStorage.getItem('gv:assistantHistory') || '[]') as HistoryEntry[]; } catch { return []; }
    };
    const [history, setHistory] = useState<HistoryEntry[]>(readHistory);
    const pushHistory = (e: Omit<HistoryEntry, 'id' | 'ts'> & Partial<Pick<HistoryEntry, 'ts'>>) => {
        const entry: HistoryEntry = { id: crypto.randomUUID(), ts: e.ts || Date.now(), type: e.type, title: e.title, subtitle: e.subtitle, payload: e.payload };
        const next = [entry, ...readHistory()].slice(0, 200);
        try { localStorage.setItem('gv:assistantHistory', JSON.stringify(next)); } catch { /* ignore */ }
        setHistory(next);
    };
    // History UI removed per request

    // Restore assistant search when coming back from a profile
    useEffect(() => {
        const handleRestore = (e: Event) => {
            const q = ((e as CustomEvent).detail as any)?.query || '';
            setMode('contacts');
            setInput(q);
            setShowSuggestions(false);
        };
        window.addEventListener('gv:assistant:restoreSearch' as any, handleRestore as any);
        return () => window.removeEventListener('gv:assistant:restoreSearch' as any, handleRestore as any);
    }, []);

    useEffect(() => {
        containerRef.current?.scrollTo({ top: containerRef.current.scrollHeight });
    }, [messages, busy]);

    // Auto-suggestion effect for contacts mode
    useEffect(() => {
        if (mode !== 'contacts') {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        const query = input.trim();
        if (!query || query.length < 2) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        // Clear existing debounce
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        // Set loading state
        setLoadingSuggestions(true);

        // Debounce the search
        debounceRef.current = setTimeout(async () => {
            try {
                const rows = activeGroupId
                    ? await listContactsForGroupCore(supabase as any, activeGroupId, { query, includeArchived: false as const, limit: 5, offset: 0 })
                    : await listContactsCore(supabase as any, { query, includeArchived: false as const, limit: 5, offset: 0 });

                setSuggestions(rows);
                setShowSuggestions(rows.length > 0);
            } catch (error) {
                console.error('Error fetching suggestions:', error);
                setSuggestions([]);
                setShowSuggestions(false);
            } finally {
                setLoadingSuggestions(false);
            }
        }, 300); // 300ms debounce

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [input, mode, activeGroupId]);

    const recordAssistantSearch = (q: string) => {
        try { sessionStorage.setItem('gv:assistantReturn', q || ''); } catch { /* ignore */ }
    };

    // Handle suggestion selection
    const selectSuggestion = (contact: any) => {
        // Record the current input for restore on back
        recordAssistantSearch(input);
        setSuggestions([]);
        setShowSuggestions(false);
        // Navigate directly to the contact detail view
        try {
            window.dispatchEvent(new CustomEvent('gv:openContact' as any, { detail: { contactId: contact.id } }));
        } catch { /* ignore */ }
    };

    const isSelected = (id: string) => selectedContacts.some(c => c.id === id);
    const toggleSelect = (contact: any) => {
        setSelectedContacts(prev => {
            const exists = prev.some(c => c.id === contact.id);
            if (exists) return prev.filter(c => c.id !== contact.id);
            return [...prev, contact];
        });
    };

    const send = async () => {
        const text = input.trim();
        // For phone/scan, sending behaves differently
        if (mode === 'phone') {
            if (!phoneDigits.trim()) return;
            sendSms(phoneDigits);
            return;
        }
        if (mode === 'transcribe') {
            // Composer send is not used for transcribe mode
            return;
        }
        if (!text) return;
        const id = crypto.randomUUID();
        const userMsg: ChatItem = { id, role: 'user', content: text, mode };
        setMessages(prev => [...prev, userMsg]);
        // Keep input when in scan mode so user can immediately tap Create
        if (mode !== 'scan') setInput('');
        setBusy(true);

        try {
            if (mode === 'contacts') {
                recordAssistantSearch(text);
                const rows = activeGroupId
                    ? await listContactsForGroupCore(supabase as any, activeGroupId, { query: text, includeArchived: false as const, limit: 25, offset: 0 })
                    : await listContactsCore(supabase as any, { query: text, includeArchived: false as const, limit: 25, offset: 0 });
                const asst: ChatItem = {
                    id: crypto.randomUUID(),
                    role: 'assistant',
                    content: rows.length ? `Found ${rows.length} contact${rows.length === 1 ? '' : 's'}:` : 'No matches',
                    mode,
                    attachments: rows.map((c: any) => ({ type: 'contact', payload: c })),
                };
                setMessages(prev => [...prev, asst]);
                // Log to history
                pushHistory({ type: 'contacts', title: `Search: ${text}`, subtitle: `${rows.length} result${rows.length === 1 ? '' : 's'}`, payload: { query: text } });
            } else if (mode === 'ai') {
                const targets = selectedContacts.map(c => ({ id: c.id, name: `${c.first_name || ''} ${c.last_name || ''}`.trim() }));
                const { data, error } = await supabase.functions.invoke('ai-database-query', { body: { query: text, contactIds: targets.map(t => t.id), targets } });
                if (error) throw new Error(error.message || 'AI query failed');
                const asst: ChatItem = { id: crypto.randomUUID(), role: 'assistant', content: JSON.stringify(data, null, 2), mode };
                setMessages(prev => [...prev, asst]);
                // Log to history
                pushHistory({ type: 'ai', title: text, subtitle: selectedContacts.length ? `${selectedContacts.length} selected` : undefined, payload: { query: text, contactIds: selectedContacts.map(c => c.id) } });
            } else if (mode === 'scan') {
                // If user typed something in scan mode, treat it as note and offer create contact
                const asst: ChatItem = {
                    id: crypto.randomUUID(),
                    role: 'assistant',
                    content: 'Do you want to create a contact from this text?',
                    mode,
                    attachments: [],
                    meta: { text }
                };
                setMessages(prev => [...prev, asst]);
            }
        } catch (e: any) {
            setMessages(prev => [...prev, { id: crypto.randomUUID(), role: 'assistant', content: e?.message || 'Error', mode }]);
        } finally {
            setBusy(false);
        }
    };

    // Handle creating a contact from text
    const handleCreateContact = () => {
        if (!input.trim()) return;
        window.sessionStorage.setItem('gv:incomingPaste', input);
        setInput('');
        // Navigate to QUICK ADD (short form) per new requirement
        window.dispatchEvent(new CustomEvent('gv:navigate', { detail: { view: 'quick-add-contact' } } as any));
        pushHistory({ type: 'create', title: 'Create contact', subtitle: input.slice(0, 80), payload: { text: input } });
    };

    // SMS quick action from within chat
    const sendSms = (phoneDigits: string) => {
        const d = (phoneDigits || '').replace(/[^\d+]/g, '');
        if (!d) return;
        try {
            const cfg = getQuickActionsConfig();
            const body = `Hey, it's ${cfg.myDisplayName}. Nice to meet you. Here's my vCard: ${cfg.myVCardUrl}`;
            window.location.href = `sms:${encodeURIComponent(d)}?&body=${encodeURIComponent(body)}`;
        } catch (e: any) {
            alert(e?.message || 'Quick actions config missing');
        }
    };

    // Camera controls for Scan mode
    const startCamera = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play();
            }
            setScanning(true);
        } catch (err: any) {
            setMessages(prev => [...prev, { id: crypto.randomUUID(), role: 'assistant', content: err?.message || 'Camera access failed', mode: 'scan' }]);
        }
    };

    const stopCamera = () => {
        try {
            streamRef.current?.getTracks().forEach(t => t.stop());
        } catch { /* ignore */ }
        streamRef.current = null;
        setScanning(false);
    };

    const capturePhoto = async () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas) return;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
        // Post image as user message
        setMessages(prev => [...prev, { id: crypto.randomUUID(), role: 'user', content: 'Scanned business card', mode: 'create', attachments: [{ type: 'image', payload: dataUrl }] }]);
        setBusy(true);
        try {
            const { data, error } = await supabase.functions.invoke('gemini-vision-parse-card', { body: { image: dataUrl } });
            if (error) throw new Error(error.message || 'Card parse failed');
            const suggestion = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
            setMessages(prev => [...prev, { id: crypto.randomUUID(), role: 'assistant', content: suggestion, mode: 'create' }]);
            // Prefill the input so switching to Create keeps the parsed text
            try { setInput(suggestion); } catch { /* ignore */ }
        } catch (e: any) {
            setMessages(prev => [...prev, { id: crypto.randomUUID(), role: 'assistant', content: e?.message || 'Parse error', mode: 'create' }]);
        } finally {
            setBusy(false);
            stopCamera();
        }
    };

    // Create contact handoff from parsed text
    const createContactFromText = (text: string) => {
        try { sessionStorage.setItem('gv:incomingPaste', text); } catch { /* ignore */ }
        window.dispatchEvent(new CustomEvent('gv:navigate', { detail: { view: 'quick-add-contact' } } as any));
    };

    // Transcribe: helpers
    // End transcription-specific code removal

    return (
        <div className="flex flex-col h-full">
            {/* Segmented control */}
            <div className="px-2 pt-3 pb-2 border-b border-white/10 bg-black/95 backdrop-blur sticky top-0 z-10">
                <div className="mx-auto w-full max-w-3xl grid grid-cols-4 gap-1 p-0.5 rounded-xl border border-gray-900 bg-gradient-to-b from-gray-950 to-black shadow-inner">
                    <button
                        onClick={() => setMode('contacts')}
                        className={`w-full px-2 py-2 md:px-3 md:py-2.5 rounded-md text-sm md:text-base font-medium transition-colors flex items-center justify-center gap-2 border ${mode === 'contacts' ? 'bg-blue-600/90 text-white border-blue-500/60 shadow ring-1 ring-blue-400/40' : 'bg-transparent text-gray-300 hover:bg-gray-900/60 border-transparent'}`}
                    >
                        <Search className="w-4 h-4 md:w-5 md:h-5" />
                        Search
                    </button>
                    <button
                        onClick={() => setMode('ai')}
                        className={`w-full px-2 py-2 md:px-3 md:py-2.5 rounded-md text-sm md:text-base font-medium transition-colors flex items-center justify-center gap-2 border ${mode === 'ai' ? 'bg-blue-600/90 text-white border-blue-500/60 shadow ring-1 ring-blue-400/40' : 'bg-transparent text-gray-300 hover:bg-gray-900/60 border-transparent'}`}
                    >
                        <Bot className="w-4 h-4 md:w-5 md:h-5" />
                        Ask AI
                    </button>
                    <button
                        onClick={() => setMode('create')}
                        title="Create"
                        aria-label="Create"
                        className={`w-full px-2 py-2 md:px-3 md:py-2.5 rounded-md text-sm md:text-base font-medium transition-colors flex items-center justify-center gap-2 border ${mode === 'create' ? 'bg-blue-600/90 text-white border-blue-500/60 shadow ring-1 ring-blue-400/40' : 'bg-transparent text-gray-300 hover:bg-gray-900/60 border-transparent'}`}
                    >
                        <UserPlus className="w-4 h-4 md:w-5 md:h-5" />
                        <span>Create</span>
                    </button>
                    <button
                        onClick={() => setMode('transcribe')}
                        title="Audio"
                        aria-label="Audio"
                        className={`w-full px-2 py-2 md:px-3 md:py-2.5 rounded-md text-sm md:text-base font-medium transition-colors flex items-center justify-center gap-2 border ${mode === 'transcribe' ? 'bg-blue-600/90 text-white border-blue-500/60 shadow ring-1 ring-blue-400/40' : 'bg-transparent text-gray-300 hover:bg-gray-900/60 border-transparent'}`}
                    >
                        <UploadCloud className="w-4 h-4 md:w-5 md:h-5" />
                        <span>Audio</span>
                    </button>
                </div>
            </div>

            {/* Selected contacts chips */}
            {selectedContacts.length > 0 && (
                <div className="px-4 pt-2 bg-black/95">
                    <div className="max-w-5xl mx-auto flex items-center flex-wrap gap-2">
                        {selectedContacts.map(c => (
                            <span key={c.id} className="inline-flex items-center gap-2 px-2 py-1 rounded-full bg-gray-900 border border-gray-700 text-gray-200 text-xs">
                                {c.profile_image_url ? (
                                    <img src={c.profile_image_url} className="w-4 h-4 rounded-full" />
                                ) : null}
                                {`${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Unnamed'}
                                <button className="ml-0.5 text-gray-400 hover:text-white" onClick={() => setSelectedContacts(prev => prev.filter(x => x.id !== c.id))}>
                                    <XIcon className="w-3 h-3" />
                                </button>
                            </span>
                        ))}
                        <button className="ml-1 text-[11px] text-gray-400 hover:text-white" onClick={() => setSelectedContacts([])}>Clear</button>
                    </div>
                </div>
            )}

            {/* Messages */}
            <div ref={containerRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-black">
                {/* Contact suggestions displayed in main area for contacts mode */}
                {mode === 'contacts' && showSuggestions && suggestions.length > 0 && (
                    <div className="mb-4">
                        <div className="text-gray-400 text-sm mb-4 font-medium">Suggested contacts:</div>
                        <div className="space-y-3">
                            {suggestions.map((contact, index) => (
                                <div
                                    key={contact.id || index}
                                    className="w-full flex items-center gap-4 px-5 py-4 bg-gray-900/80 hover:bg-gray-800/80 border border-gray-800/50 rounded-xl transition-all duration-200 hover:border-gray-700/50 hover:shadow-lg backdrop-blur-sm"
                                >
                                    {/* Profile picture */}
                                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-gray-600 to-gray-700 flex items-center justify-center overflow-hidden flex-shrink-0 shadow-md">
                                        {contact.profile_image_url ? (
                                            <img
                                                src={contact.profile_image_url}
                                                alt=""
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <span className="text-white text-lg font-semibold">
                                                {contact.first_name?.[0]}{contact.last_name?.[0]}
                                            </span>
                                        )}
                                    </div>

                                    {/* Contact info */}
                                    <button onClick={() => selectSuggestion(contact)} className="flex-1 text-left">
                                        <div className="text-white font-semibold text-base mb-1">
                                            {[contact.first_name, contact.last_name].filter(Boolean).join(' ') || 'Unnamed'}
                                        </div>
                                        {contact.company && (
                                            <div className="text-gray-400 text-sm font-medium">{contact.company}</div>
                                        )}
                                    </button>
                                    {/* Select toggle */}
                                    <button
                                        title={isSelected(contact.id) ? 'Deselect' : 'Select'}
                                        onClick={(e) => { e.stopPropagation(); toggleSelect(contact); }}
                                        className={`ml-2 inline-flex items-center justify-center w-9 h-9 rounded-md border ${isSelected(contact.id) ? 'border-blue-600 bg-blue-600/20 text-blue-300' : 'border-gray-700 bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
                                    >
                                        <CheckIcon className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                        {loadingSuggestions && (
                            <div className="text-center text-gray-400 py-6">
                                <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
                                Loading contacts...
                            </div>
                        )}
                    </div>
                )}

                {/* Show default message only when no suggestions in contacts mode */}
                {mode === 'contacts' && !showSuggestions && !messages.length && (
                    <div className="text-gray-500">Start typing to search for contacts by name, email, or company...</div>
                )}

                {/* Top region for Audio (transcribe) results */}
                {mode === 'transcribe' && (
                    <div className="mb-4">
                        <TranscribePage userId={user?.id} />
                    </div>
                )}

                {/* Create mode: Show embedded Quick Add Contact form */}
                {mode === 'create' && (
                    <div className="mb-4">
                        <EmbeddedQuickAddContact
                            user={user}
                            onSaved={(contactId) => {
                                // After contact is created, you could navigate to it or show success message
                                console.log('Contact created with ID:', contactId);
                            }}
                        />
                    </div>
                )}

                {/* Show AI/other mode messages (exclude transcribe and create placeholders) */}
                {mode !== 'contacts' && mode !== 'transcribe' && mode !== 'create' && !messages.length && (
                    <div className="text-gray-500">
                        {mode === 'ai' ? 'Ask me any question about your data...' :
                            'Start typing to search for contacts by name, email, or company...'}
                    </div>
                )}

                {messages.filter(m => m.mode === mode && mode !== 'create').map(m => (
                    <div key={m.id} className={`max-w-3xl ${m.role === 'user' ? 'ml-auto text-right' : ''}`}>
                        <div className={`${m.role === 'user' ? 'bg-blue-600 text-white' : 'bg-gray-900 text-gray-100'} inline-block px-3 py-2 border ${m.role === 'user' ? 'border-blue-500' : 'border-gray-800'} whitespace-pre-wrap`}>{m.content}</div>
                        {m.attachments?.length ? (
                            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                {m.attachments.map((a, i) => a.type === 'contact' ? (
                                    <button key={i} className="text-left bg-gray-900 border border-gray-800 p-3 hover:bg-gray-800" onClick={() => {
                                        // Remember current search so we can restore it when returning
                                        if (mode === 'contacts') {
                                            try { sessionStorage.setItem('gv:assistantReturn', input || ''); } catch { }
                                        }
                                        // Navigate directly to contact detail
                                        try { window.dispatchEvent(new CustomEvent('gv:openContact' as any, { detail: { contactId: a.payload.id } })); } catch { }
                                    }}>
                                        <div className="font-medium text-white">{[a.payload.first_name, a.payload.last_name].filter(Boolean).join(' ') || 'Unnamed'}</div>
                                        <div className="text-xs text-gray-400">{a.payload.company || a.payload.email || a.payload.phone || '—'}</div>
                                        {a.payload.phone ? (
                                            <div className="mt-2">
                                                <button className="text-xs px-2 py-1 border border-gray-700 text-gray-300 hover:bg-gray-800 inline-flex items-center gap-1" onClick={(e) => { e.stopPropagation(); sendSms(a.payload.phone); }}>
                                                    <PhoneIcon className="w-3 h-3" /> SMS
                                                </button>
                                            </div>
                                        ) : null}
                                    </button>
                                ) : null)}
                            </div>
                        ) : null}
                    </div>
                ))}
                {busy && (
                    <div className="text-gray-400 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Working…</div>
                )}
            </div>

            {/* Input */}
            <div className={`p-4 ${mode === 'transcribe' ? '' : 'border-t border-white/10'}`}>
                <div className="max-w-4xl mx-auto w-full">

                    {/* Scan controls available from Create mode - removed since embedded form has its own scan button */}

                    {/* Transcribe tab */}
                    {mode === 'transcribe' && null}

                    {/* ChatGPT composer (hidden in transcribe and create modes) */}
                    {mode !== 'transcribe' && mode !== 'create' && (
                        <div className="relative">
                            <div className="bg-[#40414f] border border-gray-600/60 rounded-3xl shadow-lg">
                                <div className="flex items-end p-3 gap-2">
                                    {/* Removed bottom-left plus button to simplify UI */}

                                    {/* Input area */}
                                    <div className="flex-1">
                                        {mode === 'phone' ? (
                                            <input
                                                value={phoneDigits}
                                                onChange={(e) => setPhoneDigits(e.target.value.replace(/[^\d+]/g, ''))}
                                                placeholder="Enter phone number…"
                                                className="w-full bg-transparent text-white placeholder-gray-400 text-base outline-none py-2"
                                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); sendSms(phoneDigits); } }}
                                            />
                                        ) : (
                                            <textarea
                                                value={input}
                                                onChange={(e) => setInput(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && !e.shiftKey) {
                                                        e.preventDefault();
                                                        if (showSuggestions && suggestions.length > 0) {
                                                            selectSuggestion(suggestions[0]);
                                                        } else {
                                                            send();
                                                        }
                                                    }
                                                }}
                                                onBlur={() => { if (mode === 'contacts') { setShowSuggestions(false); setSuggestions([]); } }}
                                                placeholder={mode === 'contacts' ? 'Search for contacts…' : mode === 'ai' ? (selectedContacts.length ? `Ask about ${selectedContacts.length} selected contact${selectedContacts.length === 1 ? '' : 's'}…` : 'Ask anything…') : mode === 'create' ? 'Paste contact details…' : mode === 'transcribe' ? 'Audio' : 'Message GriswoldVault…'}
                                                className="w-full bg-transparent text-white placeholder-gray-400 text-base outline-none resize-none py-2 max-h-[200px] overflow-hidden"
                                                rows={1}
                                                style={{
                                                    height: 'auto',
                                                    minHeight: '24px',
                                                }}
                                                onInput={(e) => {
                                                    const target = e.target as HTMLTextAreaElement;
                                                    target.style.height = '24px';
                                                    target.style.height = target.scrollHeight + 'px';
                                                }}
                                            />
                                        )}
                                    </div>

                                    {/* Send button - circular and positioned at the right */}
                                    <button
                                        onClick={() => {
                                            if (mode === 'phone') {
                                                sendSms(phoneDigits);
                                            } else if (mode === 'create') {
                                                handleCreateContact();
                                            } else if (mode === 'transcribe') {
                                                return; // no-op; use the Transcribe button above
                                            } else if (mode === 'contacts' && showSuggestions && suggestions.length > 0) {
                                                selectSuggestion(suggestions[0]);
                                            } else {
                                                send();
                                            }
                                        }}
                                        disabled={busy || mode === 'transcribe' || (mode === 'create' ? !input.trim() : !input.trim())}
                                        className="w-8 h-8 rounded-full bg-white text-black flex items-center justify-center disabled:opacity-40 disabled:bg-gray-500 hover:bg-gray-100 transition-colors flex-shrink-0"
                                        title="Send"
                                    >
                                        <ArrowRight className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Transcription-specific modals removed; managed inside TranscribePage if needed */}
        </div>
    );
}
